import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from '../hooks/useSocket';

const C = {
  bg:'#0f172a', bg2:'#1e293b', border:'#334155',
  text:'#f1f5f9', sub:'#94a3b8', muted:'#475569',
  blue:'#0ea5e9', green:'#22c55e', yellow:'#f59e0b', red:'#ef4444'
};

const s = {
  title:  { fontSize:22, fontWeight:600, color:C.text, marginBottom:4 },
  sub:    { fontSize:13, color:C.muted, marginBottom:24 },
  grid4:  { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 },
  kpi:    { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, padding:16 },
  kpiV:   (c=C.blue) => ({ fontSize:28, fontWeight:700, color:c }),
  kpiL:   { fontSize:11, color:C.muted, marginTop:2 },
  filters:{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' },
  inp:    { background:C.bg2, border:`1px solid ${C.border}`, color:C.text,
            borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none' },
  sel:    { background:C.bg2, border:`1px solid ${C.border}`, color:C.text,
            borderRadius:8, padding:'8px 12px', fontSize:13 },
  btnSm:  { background:C.bg2, color:C.sub, border:`1px solid ${C.border}`,
            borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:12 },
  card:   { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, padding:0,
            marginBottom:12, overflow:'hidden' },
  msgHdr: { display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'12px 16px', borderBottom:`1px solid ${C.border}` },
  msgBdy: { padding:'12px 16px' },
  badge:  (c) => ({ background:c+'22', color:c, padding:'2px 10px',
                    borderRadius:12, fontSize:11, fontWeight:600 }),
  dir:    (d) => ({
            display:'inline-flex', alignItems:'center', gap:4,
            background: d==='in'?'rgba(34,197,94,.1)':'rgba(14,165,233,.1)',
            color: d==='in'?C.green:C.blue,
            padding:'2px 10px', borderRadius:12, fontSize:11, fontWeight:600
          }),
  tText:  { fontSize:14, color:C.text, lineHeight:1.6, marginTop:6 },
  meta:   { fontSize:11, color:C.muted, marginTop:4 },
  pulse:  { display:'inline-block', width:8, height:8, borderRadius:'50%',
            background:C.green, marginRight:6, animation:'pulse 2s infinite' },
  live:   { display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
            background:'rgba(34,197,94,.05)', border:`1px solid rgba(34,197,94,.2)`,
            borderRadius:8, marginBottom:16, fontSize:12, color:C.green },
  pag:    { display:'flex', gap:8, marginTop:16, alignItems:'center', fontSize:13, color:C.muted },
  pgBtn:  (ok) => ({ background:ok?C.blue:'transparent', color:ok?'#fff':C.muted,
                     border:`1px solid ${C.border}`, borderRadius:6,
                     padding:'6px 14px', cursor:ok?'pointer':'default', fontSize:12 }),
  searchBox: { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12,
               padding:20, marginBottom:20 },
  highlight: { background:'rgba(251,191,36,.2)', color:'#fbbf24',
               padding:'0 2px', borderRadius:2 },
  nodata: { color:C.muted, textAlign:'center', padding:'40px 0', fontSize:13 },
  retBtn: { background:'transparent', color:C.yellow, border:`1px solid rgba(245,158,11,.3)`,
            borderRadius:6, padding:'3px 8px', fontSize:11, cursor:'pointer' },
  tabRow: { display:'flex', gap:0, borderBottom:`1px solid ${C.border}`, marginBottom:20 },
  tab:    (a) => ({ padding:'8px 18px', cursor:'pointer', fontSize:13,
                    color:a?C.blue:C.sub, background:'none', border:'none',
                    borderBottom:`2px solid ${a?C.blue:'transparent'}` }),
};

function KpiCard({ value, label, color }) {
  return (
    <div style={s.kpi}>
      <div style={s.kpiV(color)}>{value ?? '–'}</div>
      <div style={s.kpiL}>{label}</div>
    </div>
  );
}

function BolhaTranscricao({ t, onRetry }) {
  const isIn   = t.direction === 'in';
  const status = t.status;
  return (
    <div style={s.card}>
      <div style={s.msgHdr}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={s.dir(t.direction)}>
            {isIn ? '⬇ Recebido' : '⬆ Enviado'}
          </span>
          <span style={{ fontSize:12, color:C.muted }}>
            {t.phone} · {t.atendente_nome || '–'} {t.contato_nome ? `· ${t.contato_nome}` : ''}
          </span>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {t.duracao_seg && (
            <span style={{ fontSize:11, color:C.muted }}>🎵 {t.duracao_seg}s</span>
          )}
          <span style={s.badge(
            status==='concluido'?C.green : status==='processando'?C.yellow :
            status==='erro'?C.red : C.muted
          )}>
            {status==='concluido'?'✓ Transcrito':status==='processando'?'⏳ Processando':
             status==='erro'?'✕ Erro':'⏸ Pendente'}
          </span>
          {status === 'erro' &&
            <button style={s.retBtn} onClick={() => onRetry(t.id)}>↻ Retry</button>
          }
        </div>
      </div>
      <div style={s.msgBdy}>
        {status === 'concluido' && t.transcricao ? (
          <>
            <div style={s.tText}>
              🎙 <em>{t.transcricao}</em>
            </div>
            <div style={s.meta}>
              {t.confianca ? `Confiança: ${(t.confianca * 100).toFixed(0)}%` : ''}
              {t.tokens_usados ? ` · ${t.tokens_usados} tokens` : ''}
              {t.created_at ? ` · ${new Date(t.created_at).toLocaleString('pt-BR')}` : ''}
            </div>
          </>
        ) : status === 'erro' ? (
          <div style={{ fontSize:13, color:C.red }}>{t.erro || 'Erro desconhecido'}</div>
        ) : (
          <div style={{ fontSize:13, color:C.muted, fontStyle:'italic' }}>Aguardando transcrição...</div>
        )}
      </div>
    </div>
  );
}

export default function Transcricoes() {
  const { socket } = useSocket();
  const [aba, setAba]         = useState('feed');
  const [stats, setStats]     = useState(null);
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [atendentes, setAt]   = useState([]);
  const [liveItems, setLive]  = useState([]);
  const [busca, setBusca]     = useState('');
  const [resultados, setRes]  = useState([]);
  const [buscando, setBusc]   = useState(false);
  const [filtros, setFiltros] = useState({
    phone:'', atendente_id:'', direction:'', status:'', ordem:'desc'
  });

  const carregarStats = () =>
    api.get('/audio/stats').then(r => setStats(r.data)).catch(()=>{});

  const carregarFeed = useCallback(() => {
    const p = new URLSearchParams({ page, limit:30, ...Object.fromEntries(
      Object.entries(filtros).filter(([,v]) => v)
    )});
    api.get(`/audio/transcricoes?${p}`)
      .then(r => { setItems(r.data.transcricoes); setTotal(r.data.total); })
      .catch(()=>{});
  }, [page, filtros]);

  useEffect(() => { carregarStats(); api.get('/atendentes').then(r=>setAt(r.data)); }, []);
  useEffect(() => { if (aba === 'feed') carregarFeed(); }, [aba, carregarFeed]);

  // Tempo real
  useEffect(() => {
    if (!socket) return;
    const h = (data) => {
      setLive(prev => [data, ...prev].slice(0, 20));
      carregarStats();
      if (aba === 'feed') carregarFeed();
    };
    socket.on('audio_transcrito', h);
    return () => socket.off('audio_transcrito', h);
  }, [socket, aba, carregarFeed]);

  const retry = async (id) => {
    await api.post(`/audio/retry/${id}`);
    carregarFeed();
  };

  const buscarTexto = async () => {
    if (!busca.trim()) return;
    setBusc(true);
    try {
      const { data } = await api.get(`/audio/buscar?q=${encodeURIComponent(busca)}&ordem=${filtros.ordem}`);
      setRes(data);
    } finally { setBusc(false); }
  };

  const totalPages = Math.ceil(total / 30);

  return (
    <div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <div style={s.title}>Transcrição de Áudios</div>
      <div style={s.sub}>Áudios recebidos e enviados são transcritos automaticamente via Gemini</div>

      {/* KPIs */}
      {stats && (
        <div style={s.grid4}>
          <KpiCard value={stats.hoje}       label="Transcrições hoje"    color={C.blue} />
          <KpiCard value={stats.concluidos} label="Concluídas (30 dias)" color={C.green} />
          <KpiCard value={stats.pendentes}  label="Na fila"              color={C.yellow} />
          <KpiCard value={stats.erros}      label="Com erro"             color={C.red} />
        </div>
      )}
      {stats && (
        <div style={{ ...s.grid4, gridTemplateColumns:'repeat(4,1fr)', marginBottom:24 }}>
          <KpiCard value={stats.recebidos}    label="Áudios recebidos" />
          <KpiCard value={stats.enviados}     label="Áudios enviados" />
          <KpiCard value={stats.duracao_media ? stats.duracao_media+'s' : '–'} label="Duração média" />
          <KpiCard value={stats.confianca_media ? (stats.confianca_media*100).toFixed(0)+'%' : '–'}
                   label="Confiança média" />
        </div>
      )}

      {/* Indicador ao vivo */}
      {liveItems.length > 0 && (
        <div style={s.live}>
          <span style={s.pulse} />
          {liveItems.length} nova(s) transcrição(ões) em tempo real
        </div>
      )}

      <div style={s.tabRow}>
        {[['feed','📋 Feed'],['busca','🔍 Buscar'],['ao-vivo','📡 Ao vivo']].map(([k,l])=>(
          <button key={k} style={s.tab(aba===k)} onClick={()=>setAba(k)}>{l}</button>
        ))}
      </div>

      {/* ABA FEED */}
      {aba === 'feed' && (
        <>
          <div style={s.filters}>
            <input style={s.inp} placeholder="Filtrar por número..."
              value={filtros.phone} onChange={e=>setFiltros(f=>({...f,phone:e.target.value}))} />
            <select style={s.sel} value={filtros.atendente_id}
              onChange={e=>setFiltros(f=>({...f,atendente_id:e.target.value}))}>
              <option value="">Todos atendentes</option>
              {atendentes.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select style={s.sel} value={filtros.direction}
              onChange={e=>setFiltros(f=>({...f,direction:e.target.value}))}>
              <option value="">Enviados e recebidos</option>
              <option value="in">Recebidos (clientes)</option>
              <option value="out">Enviados (atendentes)</option>
            </select>
            <select style={s.sel} value={filtros.status}
              onChange={e=>setFiltros(f=>({...f,status:e.target.value}))}>
              <option value="">Todos status</option>
              <option value="concluido">Transcritos</option>
              <option value="pendente">Na fila</option>
              <option value="erro">Com erro</option>
            </select>
            <select style={s.sel} value={filtros.ordem}
              onChange={e=>setFiltros(f=>({...f,ordem:e.target.value}))}>
              <option value="desc">Mais recentes primeiro</option>
              <option value="asc">Mais antigas primeiro</option>
            </select>
          </div>

          {items.length === 0
            ? <div style={s.nodata}>Nenhum áudio encontrado com estes filtros.</div>
            : items.map(t => <BolhaTranscricao key={t.id} t={t} onRetry={retry} />)
          }

          <div style={s.pag}>
            <button style={s.pgBtn(page>1)} disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Ant.</button>
            <span>Página {page} de {totalPages} · {total} registros</span>
            <button style={s.pgBtn(page<totalPages)} disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Próx. →</button>
          </div>
        </>
      )}

      {/* ABA BUSCA */}
      {aba === 'busca' && (
        <>
          <div style={s.searchBox}>
            <div style={{ fontSize:13, color:C.sub, marginBottom:12 }}>
              Busca full-text no conteúdo das transcrições
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <input style={{ ...s.inp, flex:1 }} value={busca}
                placeholder="ex: matrícula, preço, horário, curso..."
                onChange={e=>setBusca(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&buscarTexto()} />
              <button style={{ background:C.blue, color:'#fff', border:'none', borderRadius:8,
                padding:'8px 20px', cursor:'pointer', fontSize:13, fontWeight:600 }}
                onClick={buscarTexto} disabled={buscando}>
                {buscando ? '...' : '🔍 Buscar'}
              </button>
            </div>
          </div>

          {resultados.map(r => (
            <div key={r.id} style={s.card}>
              <div style={s.msgHdr}>
                <span style={s.dir(r.direction)}>{r.direction==='in'?'⬇ Recebido':'⬆ Enviado'}</span>
                <span style={{ fontSize:12, color:C.muted }}>{r.phone} · {r.atendente_nome}</span>
                <span style={{ fontSize:11, color:C.muted }}>
                  {r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : ''}
                </span>
              </div>
              <div style={s.msgBdy}>
                <div style={s.tText}
                  dangerouslySetInnerHTML={{ __html:
                    (r.trecho_destacado || r.transcricao || '')
                      .replace(/<b>/g,'<mark style="background:rgba(251,191,36,.25);color:#fbbf24;padding:0 2px;border-radius:2px">')
                      .replace(/<\/b>/g,'</mark>')
                  }} />
              </div>
            </div>
          ))}
          {busca && resultados.length === 0 && !buscando && (
            <div style={s.nodata}>Nenhuma transcrição encontrada para "{busca}"</div>
          )}
        </>
      )}

      {/* ABA AO VIVO */}
      {aba === 'ao-vivo' && (
        <>
          <div style={{ fontSize:13, color:C.sub, marginBottom:16 }}>
            Áudios sendo transcritos agora — atualiza automaticamente
          </div>
          {liveItems.length === 0
            ? <div style={s.nodata}>Aguardando novos áudios...</div>
            : liveItems.map((t, i) => (
                <div key={i} style={s.card}>
                  <div style={s.msgHdr}>
                    <span style={s.dir(t.direction)}>{t.direction==='in'?'⬇ Recebido':'⬆ Enviado'}</span>
                    <span style={{ fontSize:12, color:C.muted }}>{t.phone}</span>
                    <span style={{ fontSize:11, color:C.muted }}>
                      {t.ts ? new Date(t.ts).toLocaleTimeString('pt-BR') : ''}
                    </span>
                  </div>
                  <div style={s.msgBdy}>
                    <div style={s.tText}>🎙 <em>{t.transcricao || 'Processando...'}</em></div>
                  </div>
                </div>
              ))
          }
        </>
      )}
    </div>
  );
}
